import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { Enemy } from './Enemy.js';

/**
 * Boss entity. Extends Enemy with 3-phase transitions, minion spawning, projectiles, and AoE.
 *
 * Lord of Dust (Stone Crypts boss):
 * - HP: 150, Damage: 15, Speed: 30, Size: 20
 *
 * Phase 1 (100-50% HP):
 *   - Melee rush + shield bash (stuns player if hit)
 *   - Summons 1 Tomb Rat every 4 seconds (max 3)
 *
 * Phase 2 (50-25% HP):
 *   - Faster movement, fires bone projectiles in 3 directions every 2 seconds
 *   - Summons Skeletons instead of Tomb Rats
 *
 * Phase 3 (25-0% HP):
 *   - Enraged: speed doubled, damage +50%
 *   - AoE slam every 3 seconds (32px radius around boss)
 *   - No more summoning
 */
export class Boss extends Enemy {
  constructor(x, y, config) {
    super(x, y, config);

    this.isBoss = true;

    // Phase tracking
    this.phase = 1;
    this.phase2Triggered = false;
    this.phase3Triggered = false;

    // Minion spawning
    this.minionSpawnTimer = 4.0;
    this.minionSpawnInterval = 4.0;
    this.maxMinions = 3;
    this.activeMinions = []; // references to spawned minion Enemy instances

    // Callback set by GameplayScene to spawn a minion
    this.onSpawnMinion = null;

    // Projectile firing (Phase 2)
    this.projectileTimer = 2.0;
    this.projectileInterval = 2.0;
    this.onFireBossProjectile = null; // callback: (x, y, dx, dy, speed, damage)

    // AoE slam (Phase 3)
    this.slamTimer = 3.0;
    this.slamInterval = 3.0;
    this.onBossSlam = null; // callback: (x, y, radius, damage)

    // Shield bash stun (Phase 1)
    this.shieldBashCooldown = 0;
    this.shieldBashInterval = 3.0;

    // Store base stats for phase transitions
    this.baseSpeed = config.speed;
    this.baseDamage = config.damage;
    this.phase2Speed = 50;
    this.phase2Damage = 20;
    this.phase3Speed = config.speed * 2;
    this.phase3Damage = Math.round(config.damage * 1.5);
  }

  /**
   * Override createGraphics to draw a larger boss sprite with a distinctive look.
   */
  createGraphics() {
    this.gfx = new Graphics();
    const offset = (TILE_SIZE - this.size) / 2;
    // Outer body
    this.gfx.rect(offset, offset, this.size, this.size);
    this.gfx.fill(this.color);
    // Inner diamond pattern to distinguish from regular enemies
    const cx = TILE_SIZE / 2;
    const cy = TILE_SIZE / 2;
    const d = this.size * 0.25;
    this.gfx.moveTo(cx, cy - d);
    this.gfx.lineTo(cx + d, cy);
    this.gfx.lineTo(cx, cy + d);
    this.gfx.lineTo(cx - d, cy);
    this.gfx.closePath();
    this.gfx.fill(0xffdd44);
    this.container.addChild(this.gfx);
  }

  /**
   * Extended update with 3-phase transitions, minion spawning, projectiles, and AoE.
   */
  update(dt, playerX, playerY, tiles, pathfinding) {
    if (!this.isAlive) return;

    const hpPct = this.hp / this.maxHp;

    // Check for phase 2 transition (50% HP)
    if (!this.phase2Triggered && hpPct <= 0.5) {
      this.phase2Triggered = true;
      this.phase = 2;
      this.speed = this.phase2Speed;
      this.damage = this.phase2Damage;
      this.minionSpawnTimer = 4.0;

      // Flash yellow to indicate phase change
      this.gfx.tint = 0xffff00;
      setTimeout(() => {
        if (this.gfx) {
          this.gfx.tint = 0xffffff;
        }
      }, 300);
    }

    // Check for phase 3 transition (25% HP)
    if (!this.phase3Triggered && hpPct <= 0.25) {
      this.phase3Triggered = true;
      this.phase = 3;
      this.speed = this.phase3Speed;
      this.damage = this.phase3Damage;

      // Flash red to indicate enrage
      this.gfx.tint = 0xff4444;
      setTimeout(() => {
        if (this.gfx) {
          this.gfx.tint = 0xff8844; // Stay slightly red to show enraged
        }
      }, 500);
    }

    // Phase 1 & 2: Minion spawning
    if (this.phase < 3) {
      this.minionSpawnTimer -= dt;
      if (this.minionSpawnTimer <= 0) {
        this.minionSpawnTimer = this.minionSpawnInterval;

        // Clean up dead minions from tracking
        this.activeMinions = this.activeMinions.filter(m => m.isAlive);

        if (this.activeMinions.length < this.maxMinions && this.onSpawnMinion) {
          this.onSpawnMinion(this);
        }
      }
    }

    // Phase 1: Shield bash cooldown
    if (this.phase === 1) {
      if (this.shieldBashCooldown > 0) {
        this.shieldBashCooldown -= dt;
      }
    }

    // Phase 2: Fire bone projectiles in 3 directions
    if (this.phase === 2) {
      this.projectileTimer -= dt;
      if (this.projectileTimer <= 0 && this.onFireBossProjectile) {
        this.projectileTimer = this.projectileInterval;
        const bossCX = this.x + TILE_SIZE / 2;
        const bossCY = this.y + TILE_SIZE / 2;
        const playerCX = playerX + TILE_SIZE / 2;
        const playerCY = playerY + TILE_SIZE / 2;
        const dx = playerCX - bossCX;
        const dy = playerCY - bossCY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0) {
          const ndx = dx / dist;
          const ndy = dy / dist;
          // Fire 3 projectiles: straight, +30 degrees, -30 degrees
          const angles = [0, Math.PI / 6, -Math.PI / 6];
          for (const angle of angles) {
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const rdx = ndx * cos - ndy * sin;
            const rdy = ndx * sin + ndy * cos;
            this.onFireBossProjectile(bossCX, bossCY, rdx, rdy, 100, this.damage);
          }
        }
      }
    }

    // Phase 3: AoE slam
    if (this.phase === 3) {
      this.slamTimer -= dt;
      if (this.slamTimer <= 0 && this.onBossSlam) {
        this.slamTimer = this.slamInterval;
        const bossCX = this.x + TILE_SIZE / 2;
        const bossCY = this.y + TILE_SIZE / 2;
        this.onBossSlam(bossCX, bossCY, 32, this.damage);
      }
    }

    // Call parent update for standard AI behavior
    super.update(dt, playerX, playerY, tiles, pathfinding);
  }

  /**
   * Check if shield bash should stun on this hit (Phase 1 only).
   * @returns {boolean} True if the bash should stun
   */
  shouldShieldBash() {
    if (this.phase !== 1) return false;
    if (this.shieldBashCooldown > 0) return false;
    this.shieldBashCooldown = this.shieldBashInterval;
    return true;
  }
}
